/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import React, {MouseEventHandler, useEffect, useRef, useState} from 'react';
import DvrdCheckbox from "./checkbox";
import {generateComponentId} from '../../..';

interface Props {
    onCheck: Function;
    onMouseEnter?: MouseEventHandler;
    onMouseLeave?: MouseEventHandler;
    checked: boolean | 'partial';
    disabled?: boolean;
    label?: string;
    labelPosition?: 'left' | 'right';
    className?: string;
    labelClassName?: string;
    checkboxClassName?: string;
    checkClassName?: string;
    errorClassName?: string;
    baseColor?: string;
    contrastColor?: string;
    error?: string | null;
    id?: string;
    checkIcon?: React.ReactNode;
    unControlled?: boolean;
    title?: string;
    useDarkestColor?: boolean;
    ref?: any;
    asRadio?: boolean;
}

export default function CheckboxController(props: Props) {
    const {
        disabled, unControlled, checked, onCheck, label, labelPosition, className, labelClassName, checkboxClassName,
        checkClassName, error, checkIcon, errorClassName, title, useDarkestColor, baseColor, contrastColor, asRadio
    } = props;
    const [isHovered, setIsHovered] = useState(false);
    const [internalValue, setInternalValue] = useState(props.checked);
    const id = useRef(generateComponentId(props.id));

    function _onCheck(evt: React.MouseEvent<Element, Event>) {
        if (disabled) return;
        const trueValues = [false, 'partial'];
        const value = unControlled ? trueValues.includes(internalValue) : trueValues.includes(checked);
        if (unControlled) {
            evt.stopPropagation();
            evt.persist();
            setInternalValue(value);
            onCheck(value, evt);
        } else onCheck(value, evt);
    }

    function onMouseEnter(evt: React.MouseEvent) {
        if (disabled) return;
        setIsHovered(true);
        props.onMouseEnter?.(evt);
    }

    function onMouseLeave(evt: React.MouseEvent) {
        setIsHovered(false);
        props.onMouseLeave?.(evt);
    }

    function addRipple() {
        const container = getContainer();
        if (!container) return;
        const ripple = getRipple();
        if (!ripple) return;
        container.classList.remove('rippling');
        ripple.addEventListener('transitionend', removeRipple);
        container.classList.add('rippling');
    }

    function removeRipple() {
        const container = getContainer();
        if (!container) return;
        const ripple = getRipple();
        if (!ripple) return;
        ripple.removeEventListener('transitionend', removeRipple);
        container.classList.remove('rippling');
    }

    function getContainer() {
        return document.getElementById(id.current);
    }

    function getRipple() {
        return getContainer()?.querySelector('div.ripple') ?? null;
    }

    useEffect(() => {
        if (unControlled && internalValue) addRipple();
        else if (!unControlled && checked) addRipple();
        return function () {
            removeRipple();
        }
    }, [internalValue, checked]);

    const isChecked = unControlled ? internalValue : checked
    return (
        <DvrdCheckbox onCheck={_onCheck} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} checked={isChecked}
                      disabled={disabled ?? false} isHovered={isHovered} label={label}
                      labelPosition={labelPosition ?? 'right'} className={className} labelClassName={labelClassName}
                      checkboxClassName={checkboxClassName} checkClassName={checkClassName} id={id.current}
                      checkIcon={checkIcon} error={error ?? null} errorClassName={errorClassName} title={title}
                      baseColor={baseColor} contrastColor={contrastColor} useDarkestColor={useDarkestColor ?? true}
                      asRadio={asRadio}/>
    )
}